import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { NisParticipantResponse } from "../../../../api/services/nisParticipants/models";
import {
  FormController,
  MuiGrid,
  MuiSelectPhoneWithTextInput,
  MuiTextField,
} from "../../../../components";
import { MAX_LENGTH_100, PAGE_IDS } from "../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import { emailValidation, isNotEmpty } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { CreateNISParticipantFormKey } from "../../modelsAndSchemas/Models";
import { createNISParticipantProfileSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateProfileWrapper } from "./CreateProfile.style";

const pageId = PAGE_IDS.CREATE_NIS_PARTICIPANT_PROFILE;

interface CreateProfileProps {
  isEditNISParticipant?: boolean;
  editNISParticipantData?: NisParticipantResponse;
}

export const CreateProfile: FC<CreateProfileProps> = ({
  isEditNISParticipant,
  editNISParticipantData,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const {
    useFetchLabel,
    commonLabels,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext();

  const {
    nisID,
    profileTitle,
    participantFirstName,
    participantLastName,
    jobTitle,
    participantEmail,
    participantWorkEmail,
    participantMobile,
  } = createNISParticipantProfileSiteCoreKeys;
  const getLabel = useFetchLabel(profileTitle);

  const getCountryIsoCode = () => {
    let countryCode = loggedInUser?.countryCode;
    if (
      isEditNISParticipant &&
      isNotEmpty(editNISParticipantData?.profile?.mobileNumber?.isoCode)
    ) {
      countryCode = editNISParticipantData?.profile?.mobileNumber?.isoCode;
    }
    return countryCode;
  };

  return (
    <CreateProfileWrapper>
      <MuiGrid className="main-container">
        <MuiGrid container>
          {isEditNISParticipant && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateNISParticipantFormKey.nisId}
              >
                <MuiTextField
                  id={`${pageId}-${CreateNISParticipantFormKey.nisId}`}
                  label={getLabel(nisID)}
                  placeholder={commonLabels.inputPlaceHolder}
                  disabled
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          )}

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.participantFirstName}
            >
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateNISParticipantFormKey.participantFirstName}`}
                label={getLabel(participantFirstName)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_100}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.participantLastName}
            >
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateNISParticipantFormKey.participantLastName}`}
                label={getLabel(participantLastName)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_100}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.jobTitle}
            >
              <MuiTextField
                id={`${pageId}-${CreateNISParticipantFormKey.jobTitle}`}
                label={getLabel(jobTitle)}
                placeholder={commonLabels.inputPlaceHolder}
                showCloseIcon
                maxLength={MAX_LENGTH_100}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.participantEmail}
            >
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateNISParticipantFormKey.participantEmail}`}
                label={getLabel(participantEmail)}
                placeholder={commonLabels.emailPlaceHolder}
                slotProps={{
                  htmlInput: {
                    ...emailValidation,
                    "aria-invalid": !!errors.participantEmail,
                  },
                }}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.participantWorkEmail}
            >
              <MuiTextField
                id={`${pageId}-${CreateNISParticipantFormKey.participantWorkEmail}`}
                label={getLabel(participantWorkEmail)}
                placeholder={commonLabels.emailPlaceHolder}
                slotProps={{
                  htmlInput: {
                    ...emailValidation,
                    "aria-invalid": !!errors.participantWorkEmail,
                  },
                }}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateNISParticipantFormKey.participantMobile}
            >
              <MuiSelectPhoneWithTextInput
                label={getLabel(participantMobile)}
                defaultSelectedCountry={getCountryIsoCode()}
                placeholder={commonLabels.telephonePlaceHolder}
                defaultValue={
                  isEditNISParticipant
                    ? getValues(CreateNISParticipantFormKey.participantMobile)
                    : ""
                }
                phoneNumberFormId={
                  CreateNISParticipantFormKey.participantMobile
                }
                countryCodeFormId={
                  CreateNISParticipantFormKey.participantMobileCountryCode
                }
                countryIsoCodeFormId={
                  CreateNISParticipantFormKey.participantMobileISOCode
                }
                searchPlaceholder={commonLabels.search}
                searchNotFound={commonLabels.noResult}
                selectLabel={commonLabels.selectPlaceHolder}
                invalidPhoneNumberMessage={fetchValidationMessage(
                  commonSiteCoreKeys.invalidPhoneNumber,
                )}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </CreateProfileWrapper>
  );
};
